import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IntelligenceFlow from '@/components/IntelligenceFlow';
import FloatingShapes from '@/components/FloatingShapes';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const tags = [
    t('home.hero.tag.customAi'),
    t('home.hero.tag.privateData'),
    t('home.hero.tag.software'),
    t('home.hero.tag.integrations'),
    t('home.hero.tag.analytics'),
    t('home.hero.tag.automation'),
  ];

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-background-50"
    >
      {/* Animated floating shapes background */}
      <FloatingShapes count={22} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={14} color="secondary" speed={0.5} />
      </div>

      {/* Background subtle dot grid */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, oklch(var(--primary-500)) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative gradient orbs */}
      <div className="absolute top-10 right-20 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl animate-[morphPulse_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-10 left-20 w-[500px] h-[500px] bg-secondary-500/8 rounded-full blur-3xl animate-[morphPulse_8s_ease-in-out_infinite_1s]" />
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl animate-[morphFloat_7s_ease-in-out_infinite]" />
      <div className="absolute top-20 left-1/2 w-64 h-64 bg-secondary-500/6 rounded-full blur-3xl animate-[morphPulse_5s_ease-in-out_infinite_2s]" />

      {/* Animated rotating ring */}
      <div className="absolute top-1/2 right-1/4 w-[600px] h-[600px] -translate-y-1/2 opacity-[0.03] pointer-events-none">
        <svg viewBox="0 0 600 600" className="w-full h-full animate-[spin_60s_linear_infinite]">
          <circle cx="300" cy="300" r="250" fill="none" stroke="oklch(var(--accent-500))" strokeWidth="0.5" strokeDasharray="8 12" />
          <circle cx="300" cy="300" r="200" fill="none" stroke="oklch(var(--secondary-500))" strokeWidth="0.5" strokeDasharray="4 16" />
          <circle cx="300" cy="300" r="150" fill="none" stroke="oklch(var(--accent-500))" strokeWidth="0.5" strokeDasharray="2 8" />
        </svg>
      </div>

      {/* Decorative background image */}
      <div className="absolute top-0 right-0 w-[50%] h-full opacity-[0.04] pointer-events-none hidden lg:block">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20technology%20network%20background%2C%20flowing%20data%20lines%20and%20nodes%2C%20soft%20violet%20and%20cyan%20gradients%20on%20white%2C%20minimalist%20enterprise%20aesthetic%2C%20subtle%20and%20sophisticated%2C%20clean%20lines%2C%20high%20detail&width=1200&height=900&seq=hero-bg-abstract-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-40 md:pt-52 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left: Content with MASSIVE logo */}
            <div
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Tag badge + slogan */}
              <div className="flex flex-col gap-2 mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100/80 rounded-full w-fit">
                  <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                  <span className="text-xs font-mono font-medium text-accent-700 uppercase tracking-wider">
                    {t('home.hero.badge')}
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground-600 tracking-wide italic">
                  {t('home.hero.slogan')}
                </p>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-semibold text-foreground-950 leading-[1.1] tracking-tight mb-6">
                {t('home.hero.title1')}{' '}
                <span className="relative inline-block">
                  {t('home.hero.titleShaped')}
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    height="8"
                    viewBox="0 0 200 8"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 6 Q50 0 100 4 Q150 8 200 2"
                      stroke="#7057FF"
                      strokeWidth="3"
                      fill="none"
                      opacity="0.7"
                    />
                  </svg>
                </span>{' '}
                {t('home.hero.title2')}
              </h1>

              {/* Core copy */}
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed max-w-xl mb-8">
                {t('home.hero.copy')}
              </p>

              {/* Supporting statement */}
              <p className="text-sm text-foreground-600 leading-relaxed max-w-lg mb-10 border-l-2 border-accent-400 pl-4">
                {t('home.hero.support')}
              </p>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/shape-your-project"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-all hover:scale-[1.02] whitespace-nowrap"
                >
                  {t('cta.shapeYourProject')}
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/solutions"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#22D3C5] text-white text-sm font-semibold rounded-md hover:bg-[#14b3a5] transition-all hover:scale-[1.02] whitespace-nowrap"
                >
                  {t('home.hero.explore')}
                </Link>
              </div>

              {/* Animated tags */}
              <div className="mt-10 flex flex-wrap gap-2">
                {tags.map(
                  (tag, i) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-xs font-mono text-foreground-600 bg-background-100 rounded-full border border-background-200/60 transition-all duration-500 hover:border-accent-300/60 hover:bg-accent-50/50 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}
                      style={{ transitionDelay: `${600 + i * 100}ms` }}
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Right: Intelligence Flow Diagram */}
            <div
              className={`flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="relative w-full max-w-[560px]">
                {/* Glow behind diagram */}
                <div className="absolute inset-0 -m-6 rounded-3xl bg-accent-500/5 blur-2xl animate-[morphPulse_4s_ease-in-out_infinite]" />
                <div className="absolute inset-0 -m-4 rounded-3xl bg-secondary-500/5 blur-xl animate-[morphPulse_5s_ease-in-out_infinite_1s]" />

                <IntelligenceFlow animated={true} className="relative z-10" />

                {/* Separate info boxes below the diagram */}
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  {/* Caption box */}
                  <div className="flex-1 px-4 py-3 bg-background-50/90 backdrop-blur-sm rounded-lg border border-background-200/60 shadow-sm text-center">
                    <p className="text-xs text-foreground-600 font-medium leading-relaxed">
                      {t('home.hero.caption')}
                    </p>
                  </div>
                  {/* Flow indicator box */}
                  <div className="px-4 py-3 bg-primary-500/10 backdrop-blur-sm rounded-lg border border-primary-300/40 shadow-sm text-center">
                    <span className="text-[11px] font-mono font-semibold text-primary-600 uppercase tracking-wider whitespace-nowrap">
                      {t('home.hero.flow')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
        <span className="text-[10px] font-mono text-foreground-500 uppercase tracking-widest">{t('home.hero.scroll')}</span>
        <div className="w-px h-8 bg-gradient-to-b from-foreground-400 to-transparent" />
      </div>
    </section>
  );
}